import React, { useCallback, useContext, useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { BuildingProfileCard } from "../../../components/onboarding/favorability/BuildingProfileCard";
import { FavorabilityDisclosure } from "../../../components/onboarding/favorability/FavorabilityDisclosure";
import { IntroductionConsentModal } from "../../../components/onboarding/favorability/IntroductionConsentModal";
import { PartnerMatchCard } from "../../../components/onboarding/favorability/PartnerMatchCard";
import { BinBblEntryForm } from "../../../components/onboarding/BinBblEntryForm";
import { ZeosUpgrade } from "../../../components/onboarding/ZeosUpgrade";
import { ZeosButton } from "../../../components/Shared/ZeosButton";
import { ZeosModal } from "../../../components/Shared/ZeosModal";
import { ZeosSpinner } from "../../../components/Shared/ZeosSpinner";
import { CarbonClearEmbed } from "../../../components/utility-link/CarbonClearEmbed";
import { FeatureFlags } from "../../../configs/FeatureFlags";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { useOnboardNavAuth } from "../../../hooks/useOnboardNavAuth";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { checkComplianceStatus } from "../../../services/helpers/PropertyHelpers";
import { ZeosFontSize } from "../../../services/helpers/ThemeHelpers";
import { createLogger } from "../../../services/Logger";
import { NavigationService } from "../../../services/nav/NavigationService";
import {
  IPartnerMatchCard,
  ISummaryRecommendationsResponse,
} from "../../../services/zeos-api/contracts/Favorability";
import { IPropertyContract } from "../../../services/zeos-api/contracts/Property";
import { CarbonClearTestEnum } from "../../../services/zeos-api/enums/AutoCompliance";
import { OnboardContainer } from "../layout/OnboardContainer";

const cardClass =
  "bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.05)] px-8 py-8 mb-[26px]";

export const SummaryScreen = () => {
  const api = useContext(ZeosApiContext);
  const log = createLogger("SummaryScreen");
  const navigate = useNavigate();
  const { handlePrev } = useOnboardNavAuth("summary");

  const [isUpdatingBinBbl, setIsUpdatingBinBbl] = useState<boolean>(false);
  const [needsBinBbl, setNeedsBinBbl] = useState<boolean>(false);
  const [offerCarbonClear, setOfferCarbonClear] = useState<boolean>();
  const [property, setProperty] = useState<IPropertyContract | null>(null);
  const [showBinBblModal, setShowBinBblModal] = useState<boolean>(false);
  const [showCarbonClearModal, setShowCarbonClearModal] =
    useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [waitCarbonClear, setWaitCarbonClear] = useState<boolean>(true);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();

  const [summary, setSummary] = useState<ISummaryRecommendationsResponse | null>(
    null
  );
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [consentPartner, setConsentPartner] = useState<{
    partnerId: number;
    partnerName: string;
  } | null>(null);
  const [isRequestingIntro, setIsRequestingIntro] = useState<boolean>(false);

  const handleUserCallback = useCallback((user: IZeosUser): void => {
    setZeosUser(user);
  }, []);

  const loadFavorabilitySummary = useCallback(
    async (propertyId: number, userId: number) => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const response = await api.Favorability.getSummary(propertyId, userId);
        if (response.data) {
          setSummary(response.data);
        } else {
          setSummaryError(
            response.message || "Unable to load recommendations."
          );
        }
      } catch (error) {
        log.error("Error loading favorability summary:", error);
        setSummaryError("Unable to load recommendations.");
      } finally {
        setSummaryLoading(false);
      }
    },
    [api, log]
  );

  useEffect(() => {
    const init = async () => {
      if (!zeosUser) {
        return;
      }
      try {
        const propertyId =
          zeosUser.customerClaims[0].companies[0].properties[0].propertyId;
        const propertyResponse = await api.Property.getProperty(propertyId);
        const propertyData = propertyResponse.data;
        setProperty(propertyData || null);
        if (propertyData) {
          await handleComplianceCheck(propertyData);
          await loadFavorabilitySummary(propertyId, zeosUser.zeosUserId);
        }
        if (!zeosUser.summaryEmailSent) {
          zeosUser.summaryEmailSent = true;
          api.Onboarding.getSendSummaryEmail(zeosUser.zeosUserId);
        }
      } catch (error) {
        log.error("Error initializing Summary screen:", error);
        setSummaryError("Unable to initialize summary.");
      } finally {
        setWaitCarbonClear(false);
      }
    };
    init();
  }, [zeosUser]);

  const handleComplianceCheck = async (propertyData: IPropertyContract) => {
    const result = await checkComplianceStatus({
      api,
      log,
      zip: propertyData.zip,
      bin: propertyData.bin,
      bbl: propertyData.bbl,
      propertyId: propertyData.propertyId ?? 0,
    });
    setNeedsBinBbl(result.needsBinBbl);
    setOfferCarbonClear(result.offerCarbonClear);
  };

  const handleBinBblUpdate = async (bin?: string, bbl?: string) => {
    if (!zeosUser || !property || !property.propertyId) {
      return;
    }
    setIsUpdatingBinBbl(true);
    try {
      if (bin || bbl) {
        const binBblUpdate = await api.Property.updateBblBin(
          property.propertyId,
          bbl,
          bin
        );
        setNeedsBinBbl(false);
        const updatedProperty = {
          ...property,
          bin: binBblUpdate.data?.bin || property.bin,
          bbl: binBblUpdate.data?.bbl || property.bbl,
        };
        setProperty(updatedProperty);
        await handleComplianceCheck(updatedProperty);
        await loadFavorabilitySummary(
          property.propertyId,
          zeosUser.zeosUserId
        );
      }
    } catch (error) {
      log.error("Error updating BIN/BBL:", error);
    } finally {
      setIsUpdatingBinBbl(false);
    }
  };

  const handleCarbonClearClosed = () => {
    const propertyId = property?.propertyId;
    if (propertyId) {
      void api.AutoCompliance.carbonClearGovernance({
        propertyId,
        type: CarbonClearTestEnum.Create,
      });
    } else {
      log.warn("CarbonClear close: missing propertyId");
    }
    setShowCarbonClearModal(false);
  };

  const handleEditAnswers = () => {
    navigate(NavigationService.buildOnboardingUrl("property", "back"));
  };

  const handleDone = () => {
    navigate("/portfolio");
  };

  const handleRequestIntroduction = (
    partnerId: number,
    partnerName: string
  ) => {
    setConsentPartner({ partnerId, partnerName });
  };

  const handleConfirmIntroduction = async () => {
    if (!consentPartner || !zeosUser || !property?.propertyId) {
      return;
    }
    setIsRequestingIntro(true);
    try {
      const response = await api.Favorability.requestIntroduction({
        propertyId: property.propertyId,
        partnerId: consentPartner.partnerId,
        zeosUserId: zeosUser.zeosUserId,
        consent: true,
      });
      if (response.data) {
        setSummary((prev) => {
          if (!prev) {
            return prev;
          }
          const matches: IPartnerMatchCard[] = prev.matches.map((m) =>
            m.partnerId === consentPartner.partnerId
              ? {
                  ...m,
                  introductionRequested: true,
                  leadId: response.data?.leadId ?? m.leadId,
                }
              : m
          );
          return { ...prev, matches };
        });
        setConsentPartner(null);
      }
    } catch (error) {
      log.error("Error requesting introduction:", error);
    } finally {
      setIsRequestingIntro(false);
    }
  };

  const binBblNotification = () => {
    return (
      <div className="flex flex-col items-left justify-left mt-6 mb-2">
        <h4
          className={`text-${ZeosFontSize.xxl} text-zeos-black font-semibold mb-2`}
        >
          Property Information Needed
        </h4>
        <p className="text-base text-zeos-black mb-3">
          To provide you with the most accurate energy insights and compliance
          information, we need your property's BIN (Building Identification
          Number) and/or BBL (Borough, Block, Lot) information.
        </p>
        <div className="w-full max-w-[240px]">
          <ZeosButton
            label="Add Property Information"
            type="button"
            mode="action"
            onPress={() => setShowBinBblModal(true)}
            hackWidth
          />
        </div>
      </div>
    );
  };

  const carbonClear = () => {
    return (
      <div className="flex flex-col items-left justify-left mt-6 mb-2">
        <h4
          className={`text-${ZeosFontSize.xxl} text-zeos-black font-semibold mb-2`}
        >
          Carbon Clear
        </h4>
        <p className="text-base text-zeos-black mb-3">
          As a NYC Local Law 97 covered building, the building owner is required
          to file a report to NYC Department of Buildings. ZEOS-AI can do this for
          you through the integrated Carbon Clear LL97 automated filing process.
          Start here:
        </p>
        <div className="w-full max-w-[240px]">
          <ZeosButton
            label="Connect to Carbon Clear"
            type="button"
            mode="action"
            onPress={() => setShowCarbonClearModal(true)}
            hackWidth
          />
        </div>
      </div>
    );
  };

  const form = (
    <>
      <ZeosModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      >
        <ZeosUpgrade onClose={() => setShowUpgradeModal(false)} />
      </ZeosModal>
      <ZeosModal isOpen={showCarbonClearModal} onClose={handleCarbonClearClosed}>
        <CarbonClearEmbed
          bin={property?.bin}
          onCloseSelector={handleCarbonClearClosed}
          propertyId={property?.propertyId}
          zeosUserId={zeosUser?.zeosUserId}
        />
      </ZeosModal>
      <BinBblEntryForm
        isOpen={showBinBblModal}
        onClose={() => setShowBinBblModal(false)}
        onSubmit={handleBinBblUpdate}
        isLoading={isUpdatingBinBbl}
        propertyName={property?.propertyName || "this property"}
      />
      <IntroductionConsentModal
        isOpen={consentPartner != null}
        partnerName={consentPartner?.partnerName || ""}
        isSubmitting={isRequestingIntro}
        onClose={() => setConsentPartner(null)}
        onConfirm={handleConfirmIntroduction}
      />

      <div className="w-full">
        {summaryLoading && (
          <div className="flex items-center gap-3 mb-6 text-zeos-black">
            <ZeosSpinner /> Loading your recommendations...
          </div>
        )}

        {summaryError && (
          <div className="bg-red-50 text-red-700 rounded-lg p-4 mb-6">
            {summaryError}
          </div>
        )}

        {summary?.profile && (
          <BuildingProfileCard
            profile={summary.profile}
            onEdit={handleEditAnswers}
          />
        )}

        <div className={cardClass}>
          <h2 className="text-[21px] font-bold text-[#2b2b2b] mb-1.5">
            Recommended Solutions &amp; Partners
          </h2>
          <p className="text-sm text-[#6b6b6b] mb-4">
            Ranked by ZEOS-AI Favorability Score — how closely each qualified
            partner matches your building, systems, compliance drivers, and
            goals
          </p>

          {/* An absent summary means the call failed or returned nothing, and it
              must read the same as "no qualifying matches" rather than leaving
              the card as a bare heading. The error banner above owns the error
              case, so this stays out of the way when one is showing. */}
          {!summaryLoading && !summaryError && (!summary || !summary.hasMatches) && (
            <div className="flex flex-col items-left justify-left mb-2">
              <h4
                className={`text-${ZeosFontSize.xxl} text-zeos-black font-semibold mb-2`}
              >
                No Qualifying Matches Yet
              </h4>
              <p className="text-base text-zeos-black mb-3">
                {summary?.emptyStateMessage ||
                  "We're expanding our partner network for your building type."}
              </p>
              <div className="w-full max-w-[240px]">
                <ZeosButton
                  label="Go to Dashboard"
                  type="button"
                  mode="action"
                  onPress={handleDone}
                  hackWidth
                />
              </div>
            </div>
          )}

          {summary?.matches?.map((match) => (
            <PartnerMatchCard
              key={match.partnerId}
              match={match}
              onRequestIntroduction={handleRequestIntroduction}
              isSubmitting={isRequestingIntro}
            />
          ))}
        </div>

        <div className={cardClass}>
          <h2 className="text-[21px] font-bold text-[#2b2b2b] mb-1.5">
            Suggested Energy Technologies
          </h2>
          <p className="text-sm text-[#6b6b6b] mb-4">
            Equipment-level opportunities identified from your building systems
          </p>
          <div className="space-y-3">
            {(summary?.technologies || []).map((tech) => (
              <div
                key={`${tech.brand}-${tech.description}`}
                className="flex items-center gap-3 text-[15px] text-[#2b2b2b]"
              >
                <FaCheck className="text-[#F5841F] font-black shrink-0" />
                <span>
                  {tech.brandUrl ? (
                    <a
                      href={tech.brandUrl}
                      className="text-[#F5841F] no-underline hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {tech.brand}
                    </a>
                  ) : (
                    <span className="font-semibold">{tech.brand}</span>
                  )}{" "}
                  {tech.description}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={cardClass}>
          <h2 className="text-[21px] font-bold text-[#2b2b2b] mb-1.5">
            Next Steps
          </h2>
          <p className="text-sm text-[#6b6b6b] mb-0">
            Head to your dashboard to explore energy insights, track LL97
            compliance, and start optimizing your property's energy use. Your
            recommendations stay available on your dashboard.
          </p>
          <FavorabilityDisclosure
            text={
              summary?.disclosure ||
              "Matches are scored by the ZEOS-AI Favorability Ranking engine using partner-provided, evidence-weighted data. ZEOS-AI may receive a referral fee when you engage a recommended partner."
            }
          />
          {needsBinBbl && binBblNotification()}
          {FeatureFlags.enableCarbonClear && waitCarbonClear && (
            <div className="flex items-center gap-2 mt-4 text-sm text-[#6b6b6b]">
              Checking Carbon Clear eligibility... <ZeosSpinner />
            </div>
          )}
          {offerCarbonClear && carbonClear()}

          <div className="flex justify-between items-center mt-[34px]">
            <button
              type="button"
              onClick={() => void handlePrev()}
              className="text-[#F5841F] font-semibold text-base hover:underline"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleDone}
              className="bg-[#F5841F] hover:bg-[#DD720F] text-white text-base font-bold px-7 py-3.5 rounded-lg shadow-[0_2px_6px_rgba(245,132,31,0.35)]"
            >
              Go to Portfolio View
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <OnboardContainer
      content={form}
      contentType="other"
      contentLayout="open"
      pageSlug="summary"
      userCallback={handleUserCallback}
    />
  );
};
